import type { TeamRole } from '../types/company';

/**
 * Role hierarchy per CONTEXT.md:
 * - OWNER: Everything + transfer ownership + manage subscription
 * - ADMIN: Manage team + settings
 * - AGENT: Manage own listings
 * - VIEWER: Read-only
 *
 * IMPORTANT: TeamRole governs company pages.
 * Platform Admin (UserRole) does NOT override company access.
 */
export const ROLE_HIERARCHY: Record<TeamRole, number> = {
  OWNER: 4,
  ADMIN: 3,
  AGENT: 2,
  VIEWER: 1,
};

/**
 * Check if user's role meets or exceeds required role
 */
export function hasPermission(userRole: TeamRole | null, requiredRole: TeamRole): boolean {
  if (!userRole) return false;
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}

/**
 * Permission-specific checks (for readability in components)
 */
export function canManageTeam(role: TeamRole | null): boolean {
  return hasPermission(role, 'ADMIN');
}

export function canManageProperties(role: TeamRole | null): boolean {
  return hasPermission(role, 'AGENT');
}

export function canManageSubscription(role: TeamRole | null): boolean {
  return hasPermission(role, 'OWNER');
}

export function canManageSettings(role: TeamRole | null): boolean {
  return hasPermission(role, 'ADMIN');
}

export function canViewAnalytics(role: TeamRole | null): boolean {
  return hasPermission(role, 'VIEWER'); // All roles can view
}

/**
 * Get human-readable role name for display
 */
export function getRoleDisplayName(role: TeamRole, locale: 'en' | 'ar' = 'en'): string {
  const names: Record<TeamRole, { en: string; ar: string }> = {
    OWNER: { en: 'Owner', ar: 'المالك' },
    ADMIN: { en: 'Admin', ar: 'مدير' },
    AGENT: { en: 'Agent', ar: 'وكيل' },
    VIEWER: { en: 'Viewer', ar: 'مشاهد' },
  };
  return names[role]?.[locale] || role;
}
